/**
 * Pathway editor store (PROD-591).
 *
 * Holds the editable graph for the Pathway Editor canvas. Each pathway
 * is keyed by `(tenantId, tlp)` so multi-tenant isolation (HC6) is
 * enforced at the persistence layer. The store hydrates from the
 * canonical `synthetic-pathway-states.ts` fixtures (PROD-572) on first
 * read; user edits land in memory and persist to localStorage under
 * `cps:${userId}:pathway:${tenantId}:${tlp}` per ADR-PROTO-006.
 *
 * Public surface:
 *   - `getPathway(tenantId, tlp)` returns the in-memory editable graph,
 *     hydrating from localStorage or fixture on first read.
 *   - `editNode(tenantId, tlp, nodeId, patch)` applies a partial node
 *     property update. Emits `pathway_edit` telemetry per HC9.
 *   - `editLabBasketItem(tenantId, tlp, nodeId, code, patch)` mutates a
 *     single lab basket item on a Labs node. Enforces the HC8 TSH
 *     read-only invariant: TSH `preselected` and `requiresConfirmation`
 *     CANNOT be toggled to false; the attempt is a no-op + a console
 *     warning. Emits `pathway_edit` telemetry on a successful edit.
 *   - `selectNode(tenantId, tlp, nodeId | null)` sets the inspector
 *     target. No telemetry; the inspector binding is a UI affordance.
 *   - `selectedNodeId(tenantId, tlp)` selector reads the current
 *     selection.
 *   - `reset(tenantId, tlp)` clears the persisted edit and re-hydrates
 *     from the fixture. Used by tests + Reset Demo (Phase 3).
 *
 * Telemetry (HC9): every accepted edit emits `pathway_edit` with the
 * full envelope per the schema in `services/telemetry/events.ts`. The
 * `mode` field is read from `useModeStore`; `pathway_edit_live` is
 * `mode === 'live'`. Tests assert via `vi.mock('.../telemetry')` per
 * the existing pattern in tests/nba-card.test.tsx.
 *
 * HC8 TSH read-only invariant (corpus is floor): TSH always stays
 * `preselected: true` AND `requiresConfirmation: true`. Attempting to
 * set either to `false` is a no-op + a console warning + does NOT emit
 * `pathway_edit`. Other lab items remain freely editable.
 *
 * HC5: zero em / en dashes. Section separators use `-` (hyphen),
 * commas, periods, or "and" / "to" instead.
 */
import { create } from 'zustand';
import {
  PATHWAY_NODE_TYPES,
  type PathwayNodeType,
} from '../services/pathway-layout';
import type { LabBasketItem } from '../types/lab-basket';
import {
  tlp1KhalidLabBasketItems,
  tlp1Step1NbaCandidates,
} from '../data/synthetic-pathway-states';
import { emitTelemetry, newCorrelationId } from '../services/telemetry';
import { useModeStore } from './mode.store';
import { useUserStore } from './user.store';
import {
  PATHWAY_IDS as PATHWAY_IDS_CONST,
  pathwayIdFor as pathwayIdForFn,
  type PathwayId as PathwayIdAlias,
} from './pathway-ids';
import type {
  ActionType as ActionTypeShape,
  EditablePathway as EditablePathwayShape,
  FormQuestion as FormQuestionShape,
  FormQuestionOption as FormQuestionOptionShape,
  FormQuestionType as FormQuestionTypeShape,
  NodePatch as NodePatchShape,
  NodePosition as NodePositionShape,
  PathwayEdge as PathwayEdgeShape,
  PathwayEditType as PathwayEditTypeShape,
  PathwayNode as PathwayNodeShape,
  PathwayRule as PathwayRuleShape,
  RuleOperator as RuleOperatorShape,
  RuleRiskTier as RuleRiskTierShape,
} from './pathway-types';
export {
  ACTION_TYPES,
  FORM_QUESTION_TYPES,
  RULE_OPERATORS,
  RULE_RISK_TIERS,
} from './pathway-types';
import { auditPayloadToFhir } from '../services/fhir-mappers/audit-event';
import {
  pathwayToPlanDefinition,
  planDefinitionToPathway,
} from '../services/fhir-mappers/pathway-to-plan-definition';
import {
  EtagConflictError,
  FhirNotFoundError,
  TenantMismatchError,
  getFhirClient,
  type FhirClient,
} from '../services/fhir';

/**
 * Pathway identifiers handled by the editor. Re-exported from
 * `state/pathway-ids.ts` so existing imports keep working.
 */
export const PATHWAY_IDS = PATHWAY_IDS_CONST;
export type PathwayId = PathwayIdAlias;
export const pathwayIdFor = pathwayIdForFn;

export type PathwayNode = PathwayNodeShape;
export type PathwayEdge = PathwayEdgeShape;
export type EditablePathway = EditablePathwayShape;
export type NodePatch = NodePatchShape;
export type NodePosition = NodePositionShape;
export type PathwayEditType = PathwayEditTypeShape;
export type ActionType = ActionTypeShape;
export type FormQuestion = FormQuestionShape;
export type FormQuestionOption = FormQuestionOptionShape;
export type FormQuestionType = FormQuestionTypeShape;
export type PathwayRule = PathwayRuleShape;
export type RuleOperator = RuleOperatorShape;
export type RuleRiskTier = RuleRiskTierShape;

/**
 * Sprint 4.3.7: deterministic UUID generator stub for tests + production.
 * Browser env uses `crypto.randomUUID()`; a counter fallback covers older
 * runtimes + node test envs that lack the API. Tests can override via
 * `__setUuidGeneratorForTests` so node ids are stable across runs.
 */
let uuidCounter = 0;
let uuidGenerator: () => string = () => {
  if (
    typeof crypto !== 'undefined' &&
    typeof (crypto as { randomUUID?: () => string }).randomUUID === 'function'
  ) {
    return (crypto as { randomUUID: () => string }).randomUUID();
  }
  uuidCounter += 1;
  return `uuid-${uuidCounter}`;
};
export function __setUuidGeneratorForTests(fn: (() => string) | null): void {
  if (fn === null) {
    uuidCounter = 0;
    uuidGenerator = () => {
      if (
        typeof crypto !== 'undefined' &&
        typeof (crypto as { randomUUID?: () => string }).randomUUID ===
          'function'
      ) {
        return (crypto as { randomUUID: () => string }).randomUUID();
      }
      uuidCounter += 1;
      return `uuid-${uuidCounter}`;
    };
    return;
  }
  uuidGenerator = fn;
}
function nextUuid(): string {
  return uuidGenerator();
}

const APEX = 'apex-cardiometabolic';

/**
 * Build the canonical Cardiometabolic Pathway with the Awell 3-tier
 * hierarchy (Sprint 4.3.7-fix-2). Each canonical fixture is now wrapped
 * in a Track + 4 Steps so the editor reads visually identical to a
 * fresh user-created pathway: BASELINE / REVIEW / 16 WEEK PROGRAM /
 * COMPLETION step cards with category pills, with the legacy activity
 * nodes (demographics / vitals / labs / decision / nba / outcome)
 * preserved as Actions inside their containing Step.
 *
 * Canonical legacy ids (`tlp1-node-demographics` etc) and the
 * version id `tlp1-glp1-v1` are preserved per HC9 strict registry so
 * existing tests + FHIR seeds continue to round-trip without renames.
 * The Track + Step ids are net new and namespaced under
 * `tlp1-track-...` / `tlp1-step-...` so they cannot collide with any
 * pre-existing fixture entry.
 */
function buildCardiometabolicPathway(): EditablePathway {
  const TRACK_ID = 'tlp1-track-1';
  const STEP_BASELINE = 'tlp1-step-baseline';
  const STEP_REVIEW = 'tlp1-step-review';
  const STEP_PROGRAM = 'tlp1-step-program';
  const STEP_COMPLETION = 'tlp1-step-completion';
  return {
    id: pathwayIdFor('cardiometabolic-pathway'),
    pathwayId: 'cardiometabolic-pathway',
    tenantId: APEX,
    nodes: [
      // Track wrapper.
      {
        id: TRACK_ID,
        nodeType: 'track',
        label: 'Cardiometabolic Pathway',
        description:
          'Apex Healthcare GLP-1 enrollment pathway. Triggered manually by the clinician at consult.',
        triggerType: 'manual',
      },
      // Step 1: Baseline (BASELINE pill).
      {
        id: STEP_BASELINE,
        nodeType: 'step',
        label: 'Baseline Visit',
        description: 'Demographics, vitals, baseline lab order.',
        parentNodeId: TRACK_ID,
        stepCategory: 'baseline',
        stepCategoryLabel: 'Baseline',
      },
      // Step 2: Review Gate (REVIEW pill, isDecision diamond).
      {
        id: STEP_REVIEW,
        nodeType: 'step',
        label: 'Step 3 enrollment gate',
        description:
          'Review Gate for GLP-1 enrollment readiness. Linked NBAs surface in the inspector.',
        parentNodeId: TRACK_ID,
        isDecision: true,
        stepCategory: 'review',
        stepCategoryLabel: 'Review Gate',
      },
      // Step 3: 16-Week Program (PROGRAM pill).
      {
        id: STEP_PROGRAM,
        nodeType: 'step',
        label: '16 Week Program',
        description: 'GLP-1 enrollment + lab follow-up + PROM dispatch.',
        parentNodeId: TRACK_ID,
        stepCategory: 'program',
        stepCategoryLabel: '16 Week Program',
        programDuration: '16 Week Program',
      },
      // Step 4: Completion (COMPLETION pill).
      {
        id: STEP_COMPLETION,
        nodeType: 'step',
        label: 'Outcome',
        description: '16-week Cardiometabolic Pathway GLP-1 enrollment outcome.',
        parentNodeId: TRACK_ID,
        stepCategory: 'completion',
        stepCategoryLabel: 'Completion',
      },
      // Action-shaped legacy nodes inside Step 1 (Baseline).
      {
        id: 'tlp1-node-demographics',
        nodeType: 'demographics',
        label: 'Demographics',
        description: 'Age, sex, BMI, tenant context.',
        parentNodeId: STEP_BASELINE,
      },
      {
        id: 'tlp1-node-vitals',
        nodeType: 'vitals',
        label: 'Vitals',
        description: 'BMI, BP, HR baseline.',
        parentNodeId: STEP_BASELINE,
      },
      {
        id: 'tlp1-node-labs',
        nodeType: 'labs',
        label: 'Lab basket',
        requiresConfirmation: true,
        labBasketItems: tlp1KhalidLabBasketItems.map((item) => ({ ...item })),
        description:
          'Cardiometabolic Pathway baseline lab basket. TSH stays preselected per ADR-001.',
        parentNodeId: STEP_BASELINE,
        // Pill carry-through for surfaces that still expect the legacy
        // node-level category (kept for backwards compatibility with
        // existing tests + the list fallback view).
        stepCategory: 'baseline',
        stepCategoryLabel: 'Baseline',
      },
      // Action-shaped legacy node inside Step 2 (Review Gate).
      {
        id: 'tlp1-node-decision',
        nodeType: 'decision',
        label: 'Step 3 enrollment gate',
        stateMachine:
          'pending -> awaiting_labs -> labs_confirmed -> ready_to_enroll. eGFR < 60 routes to endo referral.',
        linkedNbaIds: tlp1Step1NbaCandidates.map((c) => c.id),
        description:
          'Decision Point for GLP-1 enrollment readiness. Linked NBAs surface in the inspector.',
        parentNodeId: STEP_REVIEW,
        stepCategory: 'review',
        stepCategoryLabel: 'Review Gate',
      },
      // Action-shaped legacy nodes inside Step 3 (16 Week Program).
      {
        id: 'tlp1-node-nba-1',
        nodeType: 'nba',
        label: 'Order baseline lab panel',
        description: 'Tier 2 review window action.',
        parentNodeId: STEP_PROGRAM,
      },
      {
        id: 'tlp1-node-nba-2',
        nodeType: 'nba',
        label: 'Dispatch MSQ baseline PROM',
        description: 'Tier 2 dispatch via JITAI.',
        parentNodeId: STEP_PROGRAM,
      },
      {
        id: 'tlp1-node-nba-3',
        nodeType: 'nba',
        label: 'Refer to endocrinology for thyroid workup',
        description: 'Tier 1 manual approve.',
        parentNodeId: STEP_PROGRAM,
      },
      // Action-shaped legacy node inside Step 4 (Completion).
      {
        id: 'tlp1-node-outcome',
        nodeType: 'outcome',
        label: 'Enrollment outcome',
        description: '16-week Cardiometabolic Pathway GLP-1 enrollment with reassessment.',
        parentNodeId: STEP_COMPLETION,
        stepCategory: 'program',
        stepCategoryLabel: '16 Week Program',
        programDuration: '16 Week Program',
      },
    ],
    edges: [
      // Track -> first Step transition.
      {
        id: 'tlp1-edge-track-baseline',
        source: TRACK_ID,
        target: STEP_BASELINE,
      },
      // Step transitions inside the Track.
      {
        id: 'tlp1-edge-baseline-review',
        source: STEP_BASELINE,
        target: STEP_REVIEW,
      },
      {
        id: 'tlp1-edge-review-program',
        source: STEP_REVIEW,
        target: STEP_PROGRAM,
      },
      {
        id: 'tlp1-edge-program-completion',
        source: STEP_PROGRAM,
        target: STEP_COMPLETION,
      },
      // Legacy activity edges retained inside their containing Steps.
      // These wire the Action-shaped nodes inside Step 1.
      {
        id: 'tlp1-edge-demo-vitals',
        source: 'tlp1-node-demographics',
        target: 'tlp1-node-vitals',
      },
      {
        id: 'tlp1-edge-vitals-labs',
        source: 'tlp1-node-vitals',
        target: 'tlp1-node-labs',
      },
      // Cross-step legacy edges retained for backwards compatibility
      // with the list fallback + FHIR mapper round-trip. Hidden at
      // the canvas drill-down level by the visibleEdges filter.
      {
        id: 'tlp1-edge-labs-decision',
        source: 'tlp1-node-labs',
        target: 'tlp1-node-decision',
      },
      {
        id: 'tlp1-edge-decision-nba-1',
        source: 'tlp1-node-decision',
        target: 'tlp1-node-nba-1',
      },
      {
        id: 'tlp1-edge-decision-nba-2',
        source: 'tlp1-node-decision',
        target: 'tlp1-node-nba-2',
      },
      {
        id: 'tlp1-edge-decision-nba-3',
        source: 'tlp1-node-decision',
        target: 'tlp1-node-nba-3',
      },
      {
        id: 'tlp1-edge-nba-1-outcome',
        source: 'tlp1-node-nba-1',
        target: 'tlp1-node-outcome',
      },
      {
        id: 'tlp1-edge-nba-2-outcome',
        source: 'tlp1-node-nba-2',
        target: 'tlp1-node-outcome',
      },
      {
        id: 'tlp1-edge-nba-3-outcome',
        source: 'tlp1-node-nba-3',
        target: 'tlp1-node-outcome',
      },
    ],
  };
}

/**
 * Stub pathway scaffold for HRT / Metabolic Syndrome / CVD Prevention
 * pathways. Each carries a placeholder Track + 4 Steps with category
 * pills + the legacy 6-rank activity nodes wired as Actions inside the
 * appropriate Steps. Phase 2 fixtures (post-PROD-591) replace the
 * placeholder copy with verified clinical content.
 *
 * Sprint 4.3.7-fix-2: previously the placeholder pathway was 5 free-
 * floating activity nodes; the migration adds the same Awell 3-tier
 * shape used by `buildCardiometabolicPathway` so the canvas reads
 * visually identical across all 4 canonical fixtures.
 */
function buildPlaceholderPathway(
  pathwayId: PathwayId,
  tenantId: string,
): EditablePathway {
  const id = pathwayIdFor(pathwayId);
  const TRACK_ID = `${id}-track-1`;
  const STEP_BASELINE = `${id}-step-baseline`;
  const STEP_REVIEW = `${id}-step-review`;
  const STEP_PROGRAM = `${id}-step-program`;
  const STEP_COMPLETION = `${id}-step-completion`;
  // HRT carries an Exclusion category at the baseline gate per the
  // existing fixture; the other pathways reuse the Cardiometabolic
  // baseline mapping so the canvas reads consistently.
  const isHrt = pathwayId === 'hrt-pathway';
  const baselineCategory = isHrt ? 'exclusion' : 'baseline';
  const baselineLabel = isHrt ? 'Exclusion' : 'Baseline';
  const trackLabel: Record<PathwayId, string> = {
    'cardiometabolic-pathway': 'Cardiometabolic Pathway',
    'hrt-pathway': 'HRT Pathway',
    'metabolic-syndrome-pathway': 'Metabolic Syndrome Pathway',
    'cvd-prevention-pathway': 'CVD Prevention Pathway',
  };
  return {
    id,
    pathwayId,
    tenantId,
    nodes: [
      {
        id: TRACK_ID,
        nodeType: 'track',
        label: trackLabel[pathwayId],
        description: `${pathwayId} placeholder Track. Phase 2 fixture lands real trigger + filter.`,
        triggerType: 'manual',
      },
      {
        id: STEP_BASELINE,
        nodeType: 'step',
        label: isHrt ? 'Exclusion criteria' : 'Baseline Visit',
        description: `${pathwayId} ${baselineLabel.toLowerCase()} step.`,
        parentNodeId: TRACK_ID,
        stepCategory: baselineCategory,
        stepCategoryLabel: baselineLabel,
      },
      {
        id: STEP_REVIEW,
        nodeType: 'step',
        label: 'Review Gate',
        description: `${pathwayId} review gate.`,
        parentNodeId: TRACK_ID,
        isDecision: true,
        stepCategory: 'review',
        stepCategoryLabel: 'Review Gate',
      },
      {
        id: STEP_PROGRAM,
        nodeType: 'step',
        label: 'Program',
        description: `${pathwayId} program enrollment placeholder.`,
        parentNodeId: TRACK_ID,
        stepCategory: 'program',
        stepCategoryLabel: 'Program',
      },
      {
        id: STEP_COMPLETION,
        nodeType: 'step',
        label: 'Completion',
        description: `${pathwayId} completion outcome.`,
        parentNodeId: TRACK_ID,
        stepCategory: 'completion',
        stepCategoryLabel: 'Completion',
      },
      // Legacy activity nodes wired as Actions inside the matching Steps.
      {
        id: `${id}-node-demographics`,
        nodeType: 'demographics',
        label: 'Demographics',
        description: `${pathwayId} demographics scaffold.`,
        parentNodeId: STEP_BASELINE,
      },
      {
        id: `${id}-node-vitals`,
        nodeType: 'vitals',
        label: 'Vitals',
        description: `${pathwayId} baseline vitals scaffold.`,
        parentNodeId: STEP_BASELINE,
      },
      {
        id: `${id}-node-labs`,
        nodeType: 'labs',
        label: 'Lab basket',
        requiresConfirmation: true,
        labBasketItems: [],
        description: `${pathwayId} lab basket placeholder. Phase 2 fixture lands the real basket.`,
        parentNodeId: STEP_BASELINE,
        stepCategory: baselineCategory,
        stepCategoryLabel: baselineLabel,
      },
      {
        id: `${id}-node-decision`,
        nodeType: 'decision',
        label: 'Decision Point',
        stateMachine: 'pending -> ready -> committed',
        linkedNbaIds: [],
        description: `${pathwayId} decision-point scaffold.`,
        parentNodeId: STEP_REVIEW,
        stepCategory: 'review',
        stepCategoryLabel: 'Review Gate',
      },
      {
        id: `${id}-node-nba-1`,
        nodeType: 'nba',
        label: `${pathwayId} NBA placeholder`,
        description: 'Phase 2 fixture lands the verbatim NBA payload.',
        parentNodeId: STEP_PROGRAM,
      },
      {
        id: `${id}-node-outcome`,
        nodeType: 'outcome',
        label: 'Outcome',
        description: `${pathwayId} projected outcome.`,
        parentNodeId: STEP_COMPLETION,
        stepCategory: 'completion',
        stepCategoryLabel: 'Completion',
      },
    ],
    edges: [
      // Step transitions inside the Track.
      {
        id: `${id}-edge-track-baseline`,
        source: TRACK_ID,
        target: STEP_BASELINE,
      },
      {
        id: `${id}-edge-baseline-review`,
        source: STEP_BASELINE,
        target: STEP_REVIEW,
      },
      {
        id: `${id}-edge-review-program`,
        source: STEP_REVIEW,
        target: STEP_PROGRAM,
      },
      {
        id: `${id}-edge-program-completion`,
        source: STEP_PROGRAM,
        target: STEP_COMPLETION,
      },
      // Legacy activity edges retained for backwards compatibility +
      // FHIR mapper round-trip.
      {
        id: `${id}-edge-demo-vitals`,
        source: `${id}-node-demographics`,
        target: `${id}-node-vitals`,
      },
      {
        id: `${id}-edge-vitals-labs`,
        source: `${id}-node-vitals`,
        target: `${id}-node-labs`,
      },
      {
        id: `${id}-edge-labs-decision`,
        source: `${id}-node-labs`,
        target: `${id}-node-decision`,
      },
      {
        id: `${id}-edge-decision-nba`,
        source: `${id}-node-decision`,
        target: `${id}-node-nba-1`,
      },
      {
        id: `${id}-edge-nba-outcome`,
        source: `${id}-node-nba-1`,
        target: `${id}-node-outcome`,
      },
    ],
  };
}

/**
 * Default pathway shape for a (tenant, pathwayId) pair. The Apex /
 * Cardiometabolic combination uses the canonical fixture; everything
 * else falls back to the placeholder scaffold.
 */
function defaultPathwayFor(
  tenantId: string,
  pathwayId: PathwayId,
): EditablePathway {
  if (tenantId === APEX && pathwayId === 'cardiometabolic-pathway') {
    return buildCardiometabolicPathway();
  }
  return buildPlaceholderPathway(pathwayId, tenantId);
}

/**
 * localStorage key for **unsaved drafts** of a pathway. Per
 * Sprint 2.2 (PROD-642), localStorage is no longer the source of truth
 * for published pathways; the FHIR client is. This key prefix
 * (`cps:draft:`) explicitly scopes the entry as a draft so the
 * read-through cache never confuses a draft with a published version.
 *
 * The function name + signature are preserved for backwards
 * compatibility with existing callers (the editor view + tests).
 *
 * **Backwards-compat note (PROD-642):** pre-2.2 keys had the shape
 * `${userId}:pathway:${tenantId}:${pathwayId}` without the `cps:draft:`
 * prefix. Existing localStorage entries from prior sessions will not be
 * matched by the new shape and will be effectively orphaned (they take
 * \~few KB each and are tied to the same userId namespace). A one-line
 * cleanup is acceptable in the developer console:
 * `Object.keys(localStorage).filter(k => /^[a-z0-9-]+:pathway:/.test(k) && !k.startsWith('cps:draft:')).forEach(k => localStorage.removeItem(k))`
 * Production users do not exist yet for this surface, so an automated
 * migration is intentionally not added (avoid silent data deletion
 * for fixtures during the prototype stage).
 */
export function pathwayStorageKey(
  userId: string,
  tenantId: string,
  pathwayId: PathwayId,
): string {
  return `cps:draft:${userId}:pathway:${tenantId}:${pathwayId}`;
}

function readPersistedPathway(
  tenantId: string,
  pathwayId: PathwayId,
): EditablePathway | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const userId = useUserStore.getState().userId;
    const raw = localStorage.getItem(
      pathwayStorageKey(userId, tenantId, pathwayId),
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as EditablePathway;
    if (parsed.id !== pathwayIdFor(pathwayId)) return null;
    return parsed;
  } catch (err) {
    console.warn(
      `[pathway.store] localStorage read failed for ${tenantId}:${pathwayId}`,
      err,
    );
    return null;
  }
}

/**
 * Persist the pathway for the current user. Throws on any underlying
 * `localStorage.setItem` failure (quota exceeded, private browsing) so
 * the calling action can roll back the in-memory state per the
 * COMPONENT-CONTRACTS §S7 live-mode failure surface (mirrors the
 * tenant.store.ts PROD-568 precedent).
 */
function persistPathway(pathway: EditablePathway): void {
  if (typeof localStorage === 'undefined') {
    // Server-render / non-browser env: nothing to persist; treat as success.
    return;
  }
  const userId = useUserStore.getState().userId;
  const key = pathwayStorageKey(userId, pathway.tenantId, pathway.pathwayId);
  localStorage.setItem(key, JSON.stringify(pathway));
}

/** Canonical persist-error message surfaced to the editor UI. */
export const PATHWAY_PERSIST_ERROR_MESSAGE =
  'Pathway edit could not be persisted. Edit will reset on refresh.';

/** Map key shape used internally to address pathways. */
function pathwayKey(tenantId: string, pathwayId: PathwayId): string {
  return `${tenantId}::${pathwayId}`;
}

/**
 * Cached published pathway entry pulled from the FHIR client. The
 * `versionId` doubles as the `If-Match` ETag the next publish needs.
 */
export interface PublishedPathwayCacheEntry {
  pathway: EditablePathway;
  /** FHIR `meta.versionId` for the resource currently in the cache. */
  versionId: string | null;
}

export interface PathwayStoreState {
  /**
   * Cached pathways keyed by `${tenantId}::${tlp}`. Carries either an
   * unsaved draft (mirrors the localStorage `cps:draft:` entry) or the
   * last-loaded published version. `loadPathway` keeps this in sync
   * with the FHIR client.
   */
  pathwaysByKey: Record<string, EditablePathway>;
  /** FHIR ETag cache for published pathways. */
  publishedByKey: Record<string, PublishedPathwayCacheEntry>;
  /** Selected node id per pathway key. `null` clears the inspector. */
  selectedNodeByKey: Record<string, string | null>;
  /**
   * Transient persist-error message. Non-null when the most recent edit
   * rolled back because `localStorage.setItem` threw (quota / private
   * browsing). Cleared by `clearPersistError`, by a successful subsequent
   * edit, or by user dismissal in the inspector UI.
   */
  persistError: string | null;
  /** Clear the transient `persistError` (called on user dismissal). */
  clearPersistError: () => void;
  /**
   * Read or hydrate a pathway synchronously. Order of resolution:
   *   1. in-memory cache (`pathwaysByKey`)
   *   2. unsaved draft in localStorage (`cps:draft:` prefix)
   *   3. fixture default (synchronous)
   *
   * The FHIR client is ASYNC and is consulted via `loadPathway` (below);
   * `getPathway` keeps its synchronous signature so React components and
   * the existing test suite do not need to await. After `loadPathway`
   * runs, subsequent `getPathway` calls return the loaded value.
   */
  getPathway: (tenantId: string, pathwayId: PathwayId) => EditablePathway;
  /**
   * Async read-through: pulls the published `PlanDefinition` from the
   * FHIR client, decodes via the mapper, populates `publishedByKey` +
   * `pathwaysByKey`, and returns the loaded pathway. Falls back to the
   * synchronous fixture if the FHIR client throws `FhirNotFoundError`
   * for the requested pathway (initial demo / first publish).
   */
  loadPathway: (
    tenantId: string,
    pathwayId: PathwayId,
    client?: FhirClient,
  ) => Promise<EditablePathway>;
  /**
   * Push the current draft (or the in-memory pathway) to the FHIR
   * client as a published `PlanDefinition`. The mapper-encoded resource
   * is sent via `createPlanDefinition` on the first publish and
   * `updatePlanDefinition` (with the cached ETag) on subsequent
   * publishes. On success the localStorage draft is cleared and the
   * FHIR-returned resource becomes the new published cache entry.
   */
  publishPathway: (
    tenantId: string,
    pathwayId: PathwayId,
    client?: FhirClient,
  ) => Promise<EditablePathway>;
  /**
   * Sprint 4.3.7: drop-from-palette → create a new node.
   *
   * The canvas resolves the React Flow drop coordinate via
   * `screenToFlowPosition` and threads it through `position`. Optional
   * `parentNodeId` resolves from the drop context (dropping inside a
   * Track container sets parent = that Track; etc.). The new node gets
   * a stable `node-{uuid}` id and a default label `New {NodeType}`. Per
   * HC9 strict registry, an `AuditEvent` with source
   * `pathway-add-node` is emitted via the active FHIR client.
   *
   * Returns the updated pathway or `null` if the operation failed.
   */
  addNode: (
    tenantId: string,
    pathwayId: PathwayId,
    init: {
      nodeType: PathwayNodeType;
      position?: NodePosition;
      parentNodeId?: string;
      label?: string;
      category?: PathwayNode['category'];
    },
  ) => EditablePathway | null;
  /**
   * Sprint 4.3.7: drag-edge-between-nodes → create a transition.
   *
   * Called by the React Flow `onConnect` handler. The new edge gets a
   * stable `edge-{uuid}` id. Per HC9 strict registry, an `AuditEvent`
   * with source `pathway-add-edge` is emitted.
   */
  addEdge: (
    tenantId: string,
    pathwayId: PathwayId,
    init: { source: string; target: string; label?: string },
  ) => EditablePathway | null;
  /**
   * Sprint 4.3.7: "+ New Pathway" flow.
   *
   * Creates a fresh pathway under the active tenant with a single
   * starter Track + Start Step + a transition. Switches the cache so
   * `getPathway(tenantId, pathwayId)` returns the new shape. Per HC9
   * strict registry, an `AuditEvent` with source `pathway-create` is
   * emitted.
   */
  createPathway: (
    tenantId: string,
    pathwayId: PathwayId,
    args: { name: string; description: string },
  ) => EditablePathway;
  /** Apply a partial node property update. */
  editNode: (
    tenantId: string,
    pathwayId: PathwayId,
    nodeId: string,
    patch: NodePatch,
  ) => EditablePathway | null;
  /** Apply a partial lab basket item update. */
  editLabBasketItem: (
    tenantId: string,
    pathwayId: PathwayId,
    nodeId: string,
    code: string,
    patch: Partial<Pick<LabBasketItem, 'preselected' | 'requiresConfirmation' | 'rationale'>>,
  ) => EditablePathway | null;
  /** Set the selection target. `null` clears the inspector. */
  selectNode: (
    tenantId: string,
    pathwayId: PathwayId,
    nodeId: string | null,
  ) => void;
  /** Read the selection target for a pathway. */
  selectedNodeId: (tenantId: string, pathwayId: PathwayId) => string | null;
  /**
   * Reset a pathway to fixture defaults. Clears the localStorage draft,
   * the in-memory cache, and the published cache for the target. Used
   * by tests + Reset Demo (Phase 3).
   */
  reset: (tenantId: string, pathwayId: PathwayId) => void;
}

/**
 * Emit `pathway_edit` telemetry. Pure helper; reads `useModeStore` for
 * the live flag so a Live Mode edit is annotated with
 * `pathway_edit_live: true` per HC1.
 */
function emitPathwayEdit(args: {
  pathwayId: string;
  nodeId: string;
  editType: PathwayEditType;
  before: unknown;
  after: unknown;
}): void {
  const mode = useModeStore.getState().mode;
  emitTelemetry('pathway_edit', {
    pathway_id: args.pathwayId,
    node_id: args.nodeId,
    edit_type: args.editType,
    before: args.before,
    after: args.after,
    mode,
    pathway_edit_live: mode === 'live',
    correlation_id: newCorrelationId(),
    ts: new Date().toISOString(),
  });
}

export const usePathwayStore = create<PathwayStoreState>((set, get) => ({
  pathwaysByKey: {},
  publishedByKey: {},
  selectedNodeByKey: {},
  persistError: null,
  clearPersistError: () => {
    if (get().persistError !== null) {
      set({ persistError: null });
    }
  },
  getPathway: (tenantId, pathwayId) => {
    const key = pathwayKey(tenantId, pathwayId);
    const cached = get().pathwaysByKey[key];
    if (cached) return cached;
    const persisted = readPersistedPathway(tenantId, pathwayId);
    const hydrated = persisted ?? defaultPathwayFor(tenantId, pathwayId);
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: hydrated,
      },
    });
    return hydrated;
  },
  loadPathway: async (tenantId, pathwayId, client) => {
    const fhir = client ?? getFhirClient();
    const key = pathwayKey(tenantId, pathwayId);
    const id = pathwayIdFor(pathwayId);
    try {
      const resource = await fhir.getPlanDefinition(id, tenantId);
      const decoded = planDefinitionToPathway(resource);
      const versionId = resource.meta?.versionId ?? null;
      // Prefer the local draft when one exists; the published copy
      // becomes the cached source of truth for future ETag writes.
      const draft = readPersistedPathway(tenantId, pathwayId);
      set({
        pathwaysByKey: {
          ...get().pathwaysByKey,
          [key]: draft ?? decoded,
        },
        publishedByKey: {
          ...get().publishedByKey,
          [key]: { pathway: decoded, versionId },
        },
      });
      return draft ?? decoded;
    } catch (err) {
      // Not-found falls back to the in-memory hydration path so the
      // editor still has something to render against the demo seed.
      // TenantMismatchError is a security signal (cross-tenant read
      // attempt or misrouted client) and MUST propagate so the caller
      // can surface or audit it; never swallow it as a hydration miss.
      // Other errors propagate as well.
      if (err instanceof FhirNotFoundError) {
        const fallback = get().getPathway(tenantId, pathwayId);
        return fallback;
      }
      throw err;
    }
  },
  publishPathway: async (tenantId, pathwayId, client) => {
    const fhir = client ?? getFhirClient();
    const key = pathwayKey(tenantId, pathwayId);
    const current = get().getPathway(tenantId, pathwayId);
    if (current.tenantId !== tenantId) {
      throw new TenantMismatchError(
        'PlanDefinition',
        current.id,
        tenantId,
        current.tenantId,
      );
    }
    const planDefinition = pathwayToPlanDefinition(current);
    const cached = get().publishedByKey[key];
    let saved;
    if (cached?.versionId) {
      try {
        saved = await fhir.updatePlanDefinition(
          planDefinition,
          tenantId,
          cached.versionId,
        );
      } catch (err) {
        if (err instanceof EtagConflictError) {
          // Surface the conflict so the editor can re-fetch + diff.
          throw err;
        }
        throw err;
      }
    } else {
      saved = await fhir.createPlanDefinition(planDefinition, tenantId);
    }
    const decoded = planDefinitionToPathway(saved);
    const versionId = saved.meta?.versionId ?? null;
    // Clear the local draft now that FHIR is the source of truth.
    if (typeof localStorage !== 'undefined') {
      try {
        const userId = useUserStore.getState().userId;
        localStorage.removeItem(
          pathwayStorageKey(userId, tenantId, pathwayId),
        );
      } catch (err) {
        console.warn(
          `[pathway.store] publishPathway draft clear failed for ${key}`,
          err,
        );
      }
    }
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: decoded,
      },
      publishedByKey: {
        ...get().publishedByKey,
        [key]: { pathway: decoded, versionId },
      },
    });
    return decoded;
  },
  addNode: (tenantId, pathwayId, init) => {
    const key = pathwayKey(tenantId, pathwayId);
    const current = get().getPathway(tenantId, pathwayId);
    const previousPathway = current;
    const id = `node-${nextUuid()}`;
    const defaultLabel = `New ${init.nodeType.charAt(0).toUpperCase()}${init.nodeType.slice(1)}`;
    const node: PathwayNode = {
      id,
      nodeType: init.nodeType,
      label: init.label ?? defaultLabel,
      description: '',
    };
    if (init.parentNodeId) node.parentNodeId = init.parentNodeId;
    if (init.category) node.category = init.category;
    if (init.position) {
      // Stash the user-intended canvas position on the node `data`-like
      // override. The layout service falls back to a hierarchical grid
      // when no explicit position is set; for hand-placed nodes we keep
      // the coordinate so the canvas snaps to the drop target.
      // We store on `description`-extension free fields by adding a
      // dedicated `__position` carrier on the node body. To avoid
      // breaking the existing PathwayNode contract, position carries
      // through React Flow node data via the canvas wrapper, NOT the
      // store. The store stays geometry-agnostic per ADR-PROTO-006.
    }
    const updated: EditablePathway = {
      ...current,
      nodes: [...current.nodes, node],
    };
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: updated,
      },
    });
    try {
      persistPathway(updated);
    } catch (err) {
      console.warn(
        `[pathway.store] persist failed; reverting addNode for ${tenantId}:${pathwayId}`,
        err,
      );
      set({
        pathwaysByKey: {
          ...get().pathwaysByKey,
          [key]: previousPathway,
        },
        persistError: PATHWAY_PERSIST_ERROR_MESSAGE,
      });
      return null;
    }
    if (get().persistError !== null) {
      set({ persistError: null });
    }
    // HC9 strict registry: emit AuditEvent. Async fire-and-forget; we
    // don't block the UI on the FHIR client (the client is hot and the
    // demo seed succeeds synchronously, but live writes round-trip).
    try {
      const audit = auditPayloadToFhir({
        recorded: new Date().toISOString(),
        source: 'pathway-add-node',
        tenantId,
        subject: {
          reference: 'Practitioner/pathway-author',
          display: 'Pathway author',
        },
        verb: 'added-node',
        verbDisplay: `Added ${init.nodeType} node`,
        object: {
          reference: `urn:cps:pathway:${pathwayId}:node:${id}`,
          display: node.label,
          typeCode: 'pathway-node',
        },
        indirectObject: {
          reference: `urn:cps:pathway:${pathwayId}`,
          display: pathwayId,
          typeCode: 'pathway-version',
        },
        action: 'C',
        outcome: '0',
      });
      void getFhirClient().createAuditEvent(audit, tenantId).catch((err) => {
        console.warn('[pathway.store] addNode AuditEvent emit failed', err);
      });
    } catch (err) {
      console.warn('[pathway.store] addNode audit assembly failed', err);
    }
    emitTelemetry('pathway_edit', {
      pathway_id: updated.id,
      node_id: id,
      edit_type: 'node_property',
      before: null,
      after: node,
      mode: useModeStore.getState().mode,
      pathway_edit_live: useModeStore.getState().mode === 'live',
      correlation_id: newCorrelationId(),
      ts: new Date().toISOString(),
    });
    return updated;
  },
  addEdge: (tenantId, pathwayId, init) => {
    const key = pathwayKey(tenantId, pathwayId);
    const current = get().getPathway(tenantId, pathwayId);
    if (init.source === init.target) {
      console.warn(
        '[pathway.store] addEdge: refusing self-loop',
        init.source,
      );
      return null;
    }
    const sourceExists = current.nodes.some((n) => n.id === init.source);
    const targetExists = current.nodes.some((n) => n.id === init.target);
    if (!sourceExists || !targetExists) {
      console.warn(
        `[pathway.store] addEdge: missing endpoint(s) src=${init.source} tgt=${init.target}`,
      );
      return null;
    }
    const duplicate = current.edges.some(
      (e) => e.source === init.source && e.target === init.target,
    );
    if (duplicate) {
      console.warn(
        `[pathway.store] addEdge: duplicate transition ${init.source}->${init.target}`,
      );
      return current;
    }
    const previousPathway = current;
    const id = `edge-${nextUuid()}`;
    const edge: PathwayEdge = {
      id,
      source: init.source,
      target: init.target,
      label: init.label,
    };
    const updated: EditablePathway = {
      ...current,
      edges: [...current.edges, edge],
    };
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: updated,
      },
    });
    try {
      persistPathway(updated);
    } catch (err) {
      console.warn(
        `[pathway.store] persist failed; reverting addEdge for ${tenantId}:${pathwayId}`,
        err,
      );
      set({
        pathwaysByKey: {
          ...get().pathwaysByKey,
          [key]: previousPathway,
        },
        persistError: PATHWAY_PERSIST_ERROR_MESSAGE,
      });
      return null;
    }
    if (get().persistError !== null) {
      set({ persistError: null });
    }
    try {
      const audit = auditPayloadToFhir({
        recorded: new Date().toISOString(),
        source: 'pathway-add-edge',
        tenantId,
        subject: {
          reference: 'Practitioner/pathway-author',
          display: 'Pathway author',
        },
        verb: 'added-edge',
        verbDisplay: 'Added pathway transition',
        object: {
          reference: `urn:cps:pathway:${pathwayId}:edge:${id}`,
          display: `${init.source} -> ${init.target}`,
          typeCode: 'pathway-edge',
        },
        indirectObject: {
          reference: `urn:cps:pathway:${pathwayId}`,
          display: pathwayId,
          typeCode: 'pathway-version',
        },
        action: 'C',
        outcome: '0',
      });
      void getFhirClient().createAuditEvent(audit, tenantId).catch((err) => {
        console.warn('[pathway.store] addEdge AuditEvent emit failed', err);
      });
    } catch (err) {
      console.warn('[pathway.store] addEdge audit assembly failed', err);
    }
    return updated;
  },
  createPathway: (tenantId, pathwayId, args) => {
    const key = pathwayKey(tenantId, pathwayId);
    const id = pathwayIdFor(pathwayId);
    const trackId = `node-${nextUuid()}`;
    const stepId = `node-${nextUuid()}`;
    const edgeId = `edge-${nextUuid()}`;
    const fresh: EditablePathway = {
      id,
      pathwayId,
      tenantId,
      name: args.name,
      description: args.description,
      nodes: [
        {
          id: trackId,
          nodeType: 'track',
          label: args.name,
          description: args.description,
          triggerType: 'manual',
        },
        {
          id: stepId,
          nodeType: 'step',
          label: 'Start',
          description: 'Pathway entry step.',
          parentNodeId: trackId,
          stepCategory: 'baseline',
          stepCategoryLabel: 'Baseline',
        },
      ],
      edges: [
        {
          id: edgeId,
          source: trackId,
          target: stepId,
        },
      ],
    };
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: fresh,
      },
    });
    try {
      persistPathway(fresh);
    } catch (err) {
      console.warn(
        `[pathway.store] createPathway persist failed for ${key}`,
        err,
      );
      set({ persistError: PATHWAY_PERSIST_ERROR_MESSAGE });
    }
    try {
      const audit = auditPayloadToFhir({
        recorded: new Date().toISOString(),
        source: 'pathway-create',
        tenantId,
        subject: {
          reference: 'Practitioner/pathway-author',
          display: 'Pathway author',
        },
        verb: 'created-pathway',
        verbDisplay: `Created pathway ${args.name}`,
        object: {
          reference: `urn:cps:pathway:${pathwayId}`,
          display: args.name,
          typeCode: 'pathway-version',
        },
        action: 'C',
        outcome: '0',
      });
      void getFhirClient().createAuditEvent(audit, tenantId).catch((err) => {
        console.warn(
          '[pathway.store] createPathway AuditEvent emit failed',
          err,
        );
      });
    } catch (err) {
      console.warn('[pathway.store] createPathway audit assembly failed', err);
    }
    return fresh;
  },
  editNode: (tenantId, pathwayId, nodeId, patch) => {
    const key = pathwayKey(tenantId, pathwayId);
    const current = get().getPathway(tenantId, pathwayId);
    const idx = current.nodes.findIndex((n) => n.id === nodeId);
    if (idx === -1) {
      console.warn(
        `[pathway.store] editNode: no node "${nodeId}" in ${tenantId}:${pathwayId}`,
      );
      return null;
    }
    const before = { ...current.nodes[idx] };
    // Sprint 4.3.10: split the patch so the `null` clear semantic for
    // `transitionRule` does not pollute the strongly-typed in-memory
    // PathwayNode (which only allows `PathwayRule | undefined`).
    const { transitionRule: rawTransitionRule, ...restPatch } = patch;
    const next: PathwayNode = { ...before, ...restPatch };
    if (rawTransitionRule === null) {
      next.transitionRule = undefined;
    } else if (rawTransitionRule !== undefined) {
      next.transitionRule = rawTransitionRule;
    }
    // Skip telemetry + persist when nothing actually changed.
    const changed =
      (patch.label !== undefined && patch.label !== before.label) ||
      (patch.requiresConfirmation !== undefined &&
        patch.requiresConfirmation !== before.requiresConfirmation) ||
      (patch.stateMachine !== undefined &&
        patch.stateMachine !== before.stateMachine) ||
      (patch.description !== undefined &&
        patch.description !== before.description) ||
      (patch.category !== undefined && patch.category !== before.category) ||
      (patch.triggerType !== undefined &&
        patch.triggerType !== before.triggerType) ||
      (patch.triggerFilter !== undefined &&
        patch.triggerFilter !== before.triggerFilter) ||
      (patch.isDecision !== undefined &&
        patch.isDecision !== before.isDecision) ||
      (patch.stepCategory !== undefined &&
        patch.stepCategory !== before.stepCategory) ||
      (patch.stepCategoryLabel !== undefined &&
        patch.stepCategoryLabel !== before.stepCategoryLabel) ||
      (patch.programDuration !== undefined &&
        patch.programDuration !== before.programDuration) ||
      (patch.assignedStakeholderId !== undefined &&
        patch.assignedStakeholderId !== before.assignedStakeholderId) ||
      (patch.actionType !== undefined &&
        patch.actionType !== before.actionType) ||
      // Sprint 4.3.9: form questions land as a list-replace; treat any
      // patch with `formQuestions` set as a real change so the inspector
      // can commit add / remove / reorder + per-question edits in one
      // call.
      patch.formQuestions !== undefined ||
      // Sprint 4.3.10: transition rule replace / clear. `null` clears,
      // an object replaces; either is a real edit.
      patch.transitionRule !== undefined;
    if (!changed) return current;

    // Snapshot the previous-state pathway BEFORE mutating so we can
    // revert atomically if persistence throws (PROD-568 precedent).
    const previousPathway = current;

    const updatedNodes = [...current.nodes];
    updatedNodes[idx] = next;
    const updated: EditablePathway = { ...current, nodes: updatedNodes };
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: updated,
      },
    });

    try {
      persistPathway(updated);
    } catch (err) {
      // Persist failed: revert in-memory state, surface persistError, and
      // do NOT emit `pathway_edit` telemetry (the edit didn't really
      // happen from the user's point of view).
      console.warn(
        `[pathway.store] persist failed; reverting editNode for ${tenantId}:${pathwayId}`,
        err,
      );
      set({
        pathwaysByKey: {
          ...get().pathwaysByKey,
          [key]: previousPathway,
        },
        persistError: PATHWAY_PERSIST_ERROR_MESSAGE,
      });
      return null;
    }

    // Successful persist: clear any stale persistError and emit telemetry.
    if (get().persistError !== null) {
      set({ persistError: null });
    }
    emitPathwayEdit({
      pathwayId: updated.id,
      nodeId,
      editType: 'node_property',
      before,
      after: next,
    });
    return updated;
  },
  editLabBasketItem: (tenantId, pathwayId, nodeId, code, patch) => {
    const key = pathwayKey(tenantId, pathwayId);
    const current = get().getPathway(tenantId, pathwayId);
    const nodeIdx = current.nodes.findIndex((n) => n.id === nodeId);
    if (nodeIdx === -1) {
      console.warn(
        `[pathway.store] editLabBasketItem: no node "${nodeId}" in ${tenantId}:${pathwayId}`,
      );
      return null;
    }
    const node = current.nodes[nodeIdx];
    if (!node.labBasketItems) {
      console.warn(
        `[pathway.store] editLabBasketItem: node "${nodeId}" has no labBasketItems`,
      );
      return null;
    }
    const itemIdx = node.labBasketItems.findIndex((i) => i.code === code);
    if (itemIdx === -1) {
      console.warn(
        `[pathway.store] editLabBasketItem: no lab item "${code}" in node "${nodeId}"`,
      );
      return null;
    }
    const beforeItem = node.labBasketItems[itemIdx];

    // HC8 corpus is floor: TSH cannot be toggled off. Same applies to
    // `requiresConfirmation`. The attempt is a no-op + warning.
    if (code === 'TSH') {
      if (patch.preselected === false) {
        console.warn(
          '[pathway.store] HC8: refusing to clear TSH.preselected. ADR-001 Layer 1 invariant.',
        );
        return current;
      }
      if (patch.requiresConfirmation === false) {
        console.warn(
          '[pathway.store] HC8: refusing to clear TSH.requiresConfirmation. ADR-001 Layer 1 invariant.',
        );
        return current;
      }
    }

    const nextItem: LabBasketItem = { ...beforeItem, ...patch };

    // Determine which edit type to emit. The schema vocabulary is closed
    // so we pick the most-specific type that applies; multi-key patches
    // route through the dedicated `lab_basket_item` enum so the dashboard
    // can see they are basket-scoped (rather than a generic
    // `node_property` edit, which would conflate them with label /
    // description / state-machine edits).
    let editType: PathwayEditType = 'lab_basket_item';
    const editedKeys: string[] = [];
    if (patch.preselected !== undefined && patch.preselected !== beforeItem.preselected) {
      editedKeys.push('preselected');
      editType = 'lab_basket_preselected';
    }
    if (
      patch.requiresConfirmation !== undefined &&
      patch.requiresConfirmation !== beforeItem.requiresConfirmation
    ) {
      editedKeys.push('requiresConfirmation');
      editType = 'lab_basket_requires_confirmation';
    }
    if (patch.rationale !== undefined && patch.rationale !== beforeItem.rationale) {
      editedKeys.push('rationale');
      editType = 'lab_basket_rationale';
    }
    if (editedKeys.length === 0) return current;
    if (editedKeys.length > 1) editType = 'lab_basket_item';

    // Snapshot the previous-state pathway BEFORE mutating so we can
    // revert atomically if persistence throws (PROD-568 precedent).
    const previousPathway = current;

    const updatedItems = [...node.labBasketItems];
    updatedItems[itemIdx] = nextItem;
    const updatedNode: PathwayNode = {
      ...node,
      labBasketItems: updatedItems,
    };
    const updatedNodes = [...current.nodes];
    updatedNodes[nodeIdx] = updatedNode;
    const updated: EditablePathway = { ...current, nodes: updatedNodes };
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: updated,
      },
    });

    try {
      persistPathway(updated);
    } catch (err) {
      // Persist failed: revert in-memory state, surface persistError, and
      // do NOT emit `pathway_edit` telemetry.
      console.warn(
        `[pathway.store] persist failed; reverting editLabBasketItem for ${tenantId}:${pathwayId}`,
        err,
      );
      set({
        pathwaysByKey: {
          ...get().pathwaysByKey,
          [key]: previousPathway,
        },
        persistError: PATHWAY_PERSIST_ERROR_MESSAGE,
      });
      return null;
    }

    // Successful persist: clear any stale persistError and emit telemetry.
    if (get().persistError !== null) {
      set({ persistError: null });
    }
    emitPathwayEdit({
      pathwayId: updated.id,
      nodeId,
      editType,
      before: beforeItem,
      after: nextItem,
    });
    return updated;
  },
  selectNode: (tenantId, pathwayId, nodeId) => {
    const key = pathwayKey(tenantId, pathwayId);
    set({
      selectedNodeByKey: {
        ...get().selectedNodeByKey,
        [key]: nodeId,
      },
    });
  },
  selectedNodeId: (tenantId, pathwayId) => {
    const key = pathwayKey(tenantId, pathwayId);
    return get().selectedNodeByKey[key] ?? null;
  },
  reset: (tenantId, pathwayId) => {
    const key = pathwayKey(tenantId, pathwayId);
    if (typeof localStorage !== 'undefined') {
      try {
        const userId = useUserStore.getState().userId;
        localStorage.removeItem(pathwayStorageKey(userId, tenantId, pathwayId));
      } catch (err) {
        console.warn(`[pathway.store] reset clear failed for ${key}`, err);
      }
    }
    const fresh = defaultPathwayFor(tenantId, pathwayId);
    const nextPublished = { ...get().publishedByKey };
    delete nextPublished[key];
    set({
      pathwaysByKey: {
        ...get().pathwaysByKey,
        [key]: fresh,
      },
      publishedByKey: nextPublished,
      selectedNodeByKey: {
        ...get().selectedNodeByKey,
        [key]: null,
      },
    });
  },
}));

/** Read-only export so the editor view can iterate node types. */
export const PATHWAY_NODE_TYPE_LIST: ReadonlyArray<PathwayNodeType> =
  PATHWAY_NODE_TYPES;
